import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import Swal from 'sweetalert2'

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private jwtHelper = new JwtHelperService();
    urlApi = "https://i1ckb2z1u5.execute-api.us-east-1.amazonaws.com/dev"
    private httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
        })
    };

    // constructor(private router: Router) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        return this._httpClient.post(this.urlApi + '/login', credentials, this.httpOptions).pipe(
            switchMap((response: any) => {
                // console.log(response);
                if (response?.statusCode === 500) {
                    const Toast = Swal.mixin({
                        toast: true,
                        position: "top-end",
                        showConfirmButton: false,
                        timer: 3000,
                        timerProgressBar: true,
                        didOpen: (toast) => {
                          toast.onmouseenter = Swal.stopTimer;
                          toast.onmouseleave = Swal.resumeTimer;
                        }
                      });
                      Toast.fire({
                        icon: "error",
                        title: JSON.parse(response.body).message
                      });
                }

                if (response?.body?.data?.AuthenticationResult?.IdToken) {
                    localStorage.setItem('AccessToken', response.body.data.AuthenticationResult.AccessToken);
                    localStorage.setItem('IdToken', response.body.data.AuthenticationResult.IdToken);
                    localStorage.setItem('RefreshToken', response.body.data.AuthenticationResult.RefreshToken);
                    // if (response.user) {
                    //     localStorage.setItem('user', JSON.stringify(response.user));
                    // }
                    this._authenticated = true;
                    this.accessToken = response.body.data.AuthenticationResult.IdToken;

                    const decodedToken = this.jwtHelper.decodeToken(response.body.data.AuthenticationResult.IdToken);
                    localStorage.setItem('user', decodedToken.email);
                    localStorage.setItem('name', decodedToken.name);

                    if (decodedToken['custom:tipo'] !== 'administrador_sitio') {
                        this._authenticated = false;
                        this.accessToken = '';
                        localStorage.clear();
                        Swal.fire({
                        icon: "info",
                        title: "Oops..",
                        text: "Este usuario no tiene acceso al sitio"
                        });
                    }
                    // console.log(decodedToken);
                    // this._userService.user = '';
                }
                // Store the access token in the local storage
                // this.accessToken = response.accessToken;

                // // Set the authenticated flag to true
                

                // // Store the user on the user service
                // this._userService.user = response.user;

                // Return a new observable with the response
                return of(response);
            })
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Sign in using the token
        return this._httpClient
            .post('api/auth/sign-in-with-token', {
                accessToken: this.accessToken,
            })
            .pipe(
                catchError(() =>
                    // Return false
                    of(false)
                ),
                switchMap((response: any) => {
                    // Replace the access token with the new one if it's available on
                    // the response object.
                    //
                    // This is an added optional step for better security. Once you sign
                    // in using the token, you should generate a new one on the server
                    // side and attach it to the response object. Then the following
                    // piece of code can replace the token with the refreshed one.
                    if (response.accessToken) {
                        this.accessToken = response.accessToken;
                    }

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = response.user;

                    // Return true
                    return of(true);
                })
            );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: {
        name: string;
        email: string;
        password: string;
        company: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this.isAuthenticated()) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        console.log(this.signInUsingToken());
        return this.signInUsingToken();
    }

    isAuthenticated(): boolean {
        const token = localStorage.getItem('IdToken');
        // Verifica si existe token y no está expirado
        return token && !this.jwtHelper.isTokenExpired(token);
    }
}
